/* ------------------------------------------------------------------ */
/*  Enums                                                             */
/* ------------------------------------------------------------------ */

export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  GROUP_ADMIN = 'GROUP_ADMIN',
  TENANT_ADMIN = 'TENANT_ADMIN',
  SALES_REP = 'SALES_REP',
}

export enum ScoringStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  SCORED = 'SCORED',
  FLAGGED = 'FLAGGED',
  FAILED = 'FAILED',
}

export enum Grade {
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
  F = 'F',
}

export enum FlagType {
  PROFANITY = 'PROFANITY',
  COMPETITOR_MENTION = 'COMPETITOR_MENTION',
  VERBAL_PRICING = 'VERBAL_PRICING',
  DISCRIMINATORY_LANGUAGE = 'DISCRIMINATORY_LANGUAGE',
  VIN_MISMATCH = 'VIN_MISMATCH',
  PII_DETECTED = 'PII_DETECTED',
  NEGATIVE_SENTIMENT = 'NEGATIVE_SENTIMENT',
  LOW_VQS_SCORE = 'LOW_VQS_SCORE',
  EXCESSIVE_FILLERS = 'EXCESSIVE_FILLERS',
  REQUIRED_PHRASE_MISSING = 'REQUIRED_PHRASE_MISSING',
}

export enum FlagSeverity {
  HARD_BLOCK = 'HARD_BLOCK',
  SOFT_WARNING = 'SOFT_WARNING',
}

export enum TenantPlan {
  DEMO = 'DEMO',
  PRO = 'PRO',
  ENTERPRISE = 'ENTERPRISE',
}

/* ------------------------------------------------------------------ */
/*  Domain types                                                       */
/* ------------------------------------------------------------------ */

export interface TenantSettings {
  branding: { primaryColor: string; logoUrl: string };
  excludeCompetitorNames: string[];
  discriminatoryPhrases: string[];
  complianceThresholds: { vqsWarnThreshold: number };
  featureFlags: {
    outcomeCorrelation: boolean;
    crmAutoFetch: boolean;
    dynamicComplianceRules: boolean;
    coachingEnabled: boolean;
  };
  enforceMfa: boolean;
}

export interface DealerGroup {
  id: string;
  name: string;
  oemBrand: string | null;
  region: string | null;
  isActive: boolean;
}

export interface GroupAggregate {
  groupId: string;
  groupName: string;
  oemBrand: string | null;
  region: string | null;
  tenantCount: number;
  totalVideos: number;
  pendingCount: number;
  averageScore: number;
  complianceFlagRate: number;
  gradeDistribution: Record<string, number>;
  signalAverages: Record<string, number>;
}

export interface LeaderboardEntry {
  tenantId: string;
  tenantName: string;
  totalVideos: number;
  averageScore: number;
  complianceFlagRate: number;
}

export interface Tenant {
  id: string;
  name: string;
  dealershipName: string | null;
  slug: string;
  plan: TenantPlan;
  isActive: boolean;
  settings: TenantSettings;
  dealerGroupId: string | null;
  createdAt: string;
}

export interface User {
  id: string;
  tenantId: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  role: UserRole;
  isActive: boolean;
  mfaEnabled: boolean;
  lastLoginAt: string | null;
  externalId: string | null;
}

export interface Video {
  id: string;
  tenantId: string;
  uploadedBy: string;
  title: string;
  videoUrl: string;
  storagePath: string | null;
  durationSeconds: number;
  fileSizeBytes: string | null;
  mimeType: string | null;
  buyerFirstName: string | null;
  dealVin: string | null;
  crmBuyerNameFetched: boolean;
  crmFetchedAt: string | null;
  dmsRecordId: string | null;
  scoringStatus: ScoringStatus;
  scoringJobId: string | null;
  scoringError: string | null;
  evaluatorRawResponse: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  uploader?: Pick<User, 'id' | 'firstName' | 'lastName' | 'email'> | null;
  videoScore?: VideoScore;
  complianceFlags?: ComplianceFlag[];
  thumbnail?: { storagePath: string } | null;
}

export interface VideoScore {
  id: string;
  videoId: string;
  compositeScore: number;
  grade: Grade;
  gradeLabel: string;
  signalZoneCoverage: number;
  signalFaceOnCamera: number;
  signalLighting: number;
  signalStability: number;
  signalDuration: number;
  signalSpeechPace: number;
  signalEnergy: number;
  signalBuyerName: number;
  signalFeatureCallout: number;
  signalCta: number;
  signalZonesMeta: Record<string, unknown> | null;
  signalSpeechMeta: Record<string, unknown> | null;
  signalEnergyMeta: Record<string, unknown> | null;
  scoredAt: string;
  scoringDurationMs: number | null;
}

export interface ComplianceFlag {
  id: string;
  videoId: string;
  flagType: FlagType;
  severity: FlagSeverity;
  isResolved: boolean;
  resolvedBy: string | null;
  resolvedAt: string | null;
  overrideReason: string | null;
  transcriptExcerpt: string | null;
  detectedAt: string;
}

export type RuleScope = 'PLATFORM' | 'GROUP' | 'TENANT';

export interface RuleConfig {
  threshold?: number;
  phraseAdditions?: string[];
  phraseRemovals?: string[];
  patternAdditions?: string[];
}

export interface ComplianceRule {
  id: string;
  scope: RuleScope;
  tenantId: string | null;
  dealerGroupId: string | null;
  flagType: FlagType;
  isEnabled: boolean;
  isOverridable: boolean;
  config: RuleConfig | null;
  createdAt: string;
  updatedAt: string;
}

export interface EffectiveRule {
  id: string;
  flagType: FlagType;
  isEnabled: boolean;
  config: RuleConfig | null;
  resolvedFrom: RuleScope;
  isOverridable: boolean;
}

export type CoachingSource = 'AUTO' | 'MANUAL';

export interface CoachingNote {
  id: string;
  tenantId: string;
  videoId: string;
  repId: string;
  source: CoachingSource;
  signalId: number | null;
  noteText: string;
  isAcknowledged: boolean;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AuditLog {
  id: string;
  tenantId: string;
  userId: string;
  action: string;
  resource: string;
  resourceId: string | null;
  flagType: FlagType | null;
  resolution: string | null;
  overrideReason: string | null;
  timestamp: string;
}

/* ------------------------------------------------------------------ */
/*  API envelope                                                      */
/* ------------------------------------------------------------------ */

export interface ApiError {
  code: string;
  message: string;
  field?: string;
}

export interface ApiResponse<T> {
  data: T;
  meta?: {
    page?: number;
    pageSize?: number;
    total?: number;
    totalPages?: number;
    totalActive?: number;
    perPage?: number;
    scoringJobId?: string;
    hasHardBlocks?: boolean;
    hasErrors?: boolean;
    abortedAtStep?: string;
    scored?: boolean;
  };
  errors?: ApiError[];
}

/* ------------------------------------------------------------------ */
/*  Stats / reporting                                                  */
/* ------------------------------------------------------------------ */

export interface TeamStats {
  totalVideos: number;
  averageScore: number | null;
  flaggedVideoCount: number;
  gradeDistribution: Record<Grade, number>;
  signalAverages: Record<string, number> | null;
}

export interface ScoringStats {
  averageScore: number | null;
  highestScore: number | null;
  totalVideos: number;
  scoredCount: number;
  pendingCount: number;
  processingCount: number;
  failedCount: number;
  flaggedVideoCount: number;
  gradeDistribution: Record<Grade, number>;
  signalAverages: Record<string, number> | null;
  thisWeekCount: number;
  lastWeekCount: number;
  insight?: string;
}

/* ------------------------------------------------------------------ */
/*  Auth                                                              */
/* ------------------------------------------------------------------ */

export interface JwtPayload {
  iss: string;
  sub: string;
  tenantId: string;
  tenantSlug: string;
  role: UserRole;
  scopes: string[];
  jti: string;
  exp: number;
  iat: number;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: User;
}
